import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { formatBaht } from '@/lib/money'
import type { PricingItem } from '@/types'
import type { PricingCalculationResult } from '@/hooks/usePricingCalculation'

export interface FormulaTransparencyProps {
  formula: PricingItem[]
  data: PricingCalculationResult | undefined
  className?: string
}

const STORAGE_KEY = 'formula_transparency_collapsed'

/**
 * FormulaTransparency — show the math being applied for every percentage item.
 *
 * EP-56 L3 — operators reconcile FlowRent against their own spreadsheets,
 * and "where did this number come from?" is the first question on any
 * cross-screen mismatch. K3 added an inline "3% × ฿5,530" detail. L3
 * expands that to a full 4-line monospace block per percentage item so
 * the user can audit the formula without opening DevTools or our source.
 *
 * Example output for {key: "common_fee", type: "percentage", rate: 3}:
 *
 *   common_fee = 3% × (rent + water + electricity)
 *              = 3% × (฿5,000 + ฿180 + ฿350)
 *              = 3% × ฿5,530
 *              = ฿165.90
 *
 * Per-user persisted Hide/Show toggle in localStorage so power users can
 * collapse the block after they've audited the math once.
 */
export function FormulaTransparency({ formula, data, className = '' }: FormulaTransparencyProps) {
  const { t } = useTranslation('properties')
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true'
    } catch {
      return false
    }
  })

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(collapsed))
    } catch {
      // Storage quota or sandboxed iframe — silent fall-through.
    }
  }, [collapsed])

  if (!data) return null

  const percentageItems = formula.filter((f) => f.type === 'percentage')
  if (percentageItems.length === 0) return null

  // Non-percentage line items that contribute to the subtotal
  const baseItems = formula.filter((f) => f.type === 'fixed' || f.type === 'metered')

  // EP-114 T013 S2-14 — container: bg-base-200 border rounded-lg p-4
  // (was mt-3 border rounded-lg with padding only on the toggle button and
  // the expanded body separately). Title is bold; the "ซ่อน"/"แสดง" collapse
  // action stays a plain-weight trailing label so it reads as secondary.
  return (
    <div className={`bg-base-200 border border-base-300 rounded-lg p-4 ${className}`}>
      <button
        type="button"
        onClick={() => setCollapsed((prev) => !prev)}
        className="w-full flex items-center justify-between text-sm font-bold"
        aria-expanded={!collapsed}
        data-testid="formula-transparency-toggle"
      >
        <span className="flex items-center gap-1">
          {collapsed ? (
            <ChevronRight className="w-4 h-4" aria-hidden="true" />
          ) : (
            <ChevronDown className="w-4 h-4" aria-hidden="true" />
          )}
          {t('formula_transparency.title')}
        </span>
        <span className="text-xs font-normal text-base-content/60">
          {collapsed ? t('formula_transparency.show') : t('formula_transparency.hide')}
        </span>
      </button>

      {!collapsed && (
        <div
          className="mt-3 pt-3 border-t border-base-300 font-mono text-xs space-y-3"
          data-testid="formula-transparency-body"
        >
          {percentageItems.map((pct) => {
            const pctRate = pct.rate ?? 0
            const baseKeys = baseItems.map((i) => i.key).join(' + ')
            const lineAmounts = baseItems
              .map((b) => {
                const lineFromData = data.items.find((line) => line.item_type === b.key)
                return lineFromData ? formatBaht(lineFromData.amount) : '฿0'
              })
              .join(' + ')
            const computedAmount = data.items.find((line) => line.item_type === pct.key)?.amount ?? 0

            return (
              <div
                key={pct.key}
                className="leading-relaxed"
                data-testid={`formula-row-${pct.key}`}
              >
                <div>
                  <span className="text-base-content/80">{pct.key}</span>
                  <span className="text-base-content/50"> = </span>
                  <span>{pctRate}% × ({baseKeys})</span>
                </div>
                <div className="pl-4">
                  <span className="text-base-content/50">= </span>
                  <span>{pctRate}% × ({lineAmounts})</span>
                </div>
                <div className="pl-4">
                  <span className="text-base-content/50">= </span>
                  <span>{pctRate}% × {formatBaht(data.subtotal_before_percentage)}</span>
                </div>
                <div className="pl-4 font-semibold">
                  <span className="text-base-content/50">= </span>
                  <span>{formatBaht(computedAmount)}</span>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
